import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Input,
  isSerialNumberValid,
} from '@rabbit/elements/shared-components';
import SageFormBuilder from 'libs/elements/shared-components/src/lib/molecules/SageFormBuilder/SageFormBuilder';
import { RequiredType } from '@rabbit/helper';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ConsumerIssueTypeHookOrigins,
  LIST_COUNTRIES,
  useManageConsumerIssueTypes,
  useSageAPI,
  useGetInstallerPeers,
} from '@rabbit/bizproc/react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { NewClaimModalSteps } from '../../../ModalNewClaim/ModalNewClaim';
import {
  DTConsumer_Public,
  DTHoldingProxy,
  DTVendable,
  FBD_HoldingProxy,
  FBD_Vendable,
  GenerateTenantRootPersonaLink,
  PersonaIdTypeSplitter,
  PersonaTypeFullKeyValues,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import {
  Caseflow_CaseCreation_Sage_Shelta_FormDataShape,
  CaseflowCaseTypes,
  RegisterCaseOrigins,
} from '@rabbit/bizproc/core';
import { OurDateTime } from '@rabbit/utils/ts';
import { AppContext, createDynamicYupSchema } from '@rabbit/app-context';
import { useGetMySagePersonas } from '@rabbit/data/portal';
import {
  countryDateFormats,
  countryDateLocalesString,
} from '@rabbit/sage/utils/consts.ts';

export interface NCCHF_BaseProps {
  onHoldingCreated: (result: any) => void;
  handleClose: () => void;
  setLoading: Dispatch<SetStateAction<boolean>>;
  onChange: (step: NewClaimModalSteps, data?: any) => void;
  data: any;
  retailerData: any | null;
}

// TODO: EXTRACT GENERIC BITS OF THIS AND NCCHF_FATBIKES INTO A SHARED COMPONENT

interface HoldingDataCase {
  purchase_time: Date | null;
  consumer_issue_type_ref: string;
  purchase_location: {
    country: string;
    docid: string;
  } | null;
  store_not_listed: boolean;
  custom_store_name: string;
  purchase_country: string;
  purchase_price?: { amount: number; currency: string | undefined };
  serial_number?: string;
  installation_date?: Date | null;
  installation_time?: string;
  installer_id?: string;
  purchase_proof?: any[];
  serial_number_proof?: any[];
  installation_attachment?: any[];
  consumer_issue_description: string;
  holding_vendable_id: string;
  internal_comment: string;
}

export function NCCHF_Base(props: NCCHF_BaseProps) {
  const { handleClose, onChange, data, onHoldingCreated, setLoading } = props;

  const formikRef = useRef(null) as any;
  const { t } = useTranslation();
  const defaultSelected = data?.productResponse || null;
  const activePremiumTenantLink = t('tenantLink');
  const caseFlowType = t('CFG_CLAIMS_FLOW') as CaseflowCaseTypes;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDatePurchaseDisabled, setIsDatePurchaseDisabled] = useState(false);
  const { installerPersona, manufacturerPersona } = useGetMySagePersonas();
  const { installerPeer, installerPeerLoading } = useGetInstallerPeers();
  const [vendableItem, setVendableItem] = useState({} as DTVendable);
  const [productLoaded, setProductLoaded] = useState(false);
  const [productData, setProductData] = useState(defaultSelected);
  const [serialNumbers, setSerialNumbers] = useState<string[]>([]);
  const [serialNumberRanges, setSerialNumberRanges] = useState<
    Array<{ start: string; end: string }>
  >([]);
  const installerUsers = installerPeerLoading
    ? [{ id: '-1', label: 'Loading...', value: 'Loading...' }]
    : installerPeer.map((installer: any, index: number) => ({
        id: `${index}`,
        label: installer?.first_name
          ? `${installer.first_name} ${installer.last_name} - ${installer.name}`
          : installer.name ?? '',
        value: installer?.docid || '',
      }));

  const { config, tenantInfo } = useContext(AppContext);

  const { initiateCaseFlowCase } = useSageAPI();

  const { consumerIssueTypeList } = useManageConsumerIssueTypes(
    ConsumerIssueTypeHookOrigins.SAGE
  );
  const consumerIssueTypeOptions = !consumerIssueTypeList?.data
    ? []
    : consumerIssueTypeList.data.map((issue) => ({
        id: issue.docid,
        value: issue.docid,
        label: issue.label,
      })) ?? [];

  const initialValues: HoldingDataCase = {
    holding_vendable_id: '',
    purchase_time: null,
    consumer_issue_type_ref: '',
    purchase_location: null,
    store_not_listed: false,
    custom_store_name: '',
    purchase_country: '',
    purchase_price: { amount: 0, currency: tenantInfo?.currency || 'USD' },
    serial_number: '',
    installation_date: null,
    installation_time: '',
    installer_id: '',
    purchase_proof: [],
    serial_number_proof: [],
    installation_attachment: [],
    consumer_issue_description: '',
    internal_comment: '',
  };

  useEffect(() => {
    (async () => {
      if (productData && productData?.value && !productLoaded) {
        setProductLoaded(true);
        const vendable = await FBD_Vendable.get(productData.value);
        if (vendable) {
          formikRef.current.setFieldValue(
            'chassis_number',
            vendable.chassis_number
          );
          formikRef.current.setFieldValue(
            'registration_number',
            vendable.registration_number
          );
          formikRef.current.setFieldValue(
            'production_date',
            vendable.production_date
          );
          console.log('vendable', vendable);
          setVendableItem(vendable);

          // Store serial numbers and ranges for validation
          if (config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED) {
            setSerialNumbers(vendable.serialNumbers || []);
            setSerialNumberRanges(vendable.serialNumberRanges || []);
          }
        }
      }
    })();
  }, [
    setProductData,
    productData,
    config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED,
  ]);

  const validationSchema = Yup.object().shape(
    data.holding
      ? {
          consumer_issue_type_ref: Yup.string()
            .trim()
            .required(t('Issue type must be selected')),
          consumer_issue_description: Yup.string().trim(),
          internal_comment: Yup.string().trim(),
        }
      : {
          holding_vendable_id: Yup.string()
            .trim()
            .required(t("Product name can't be empty")),
          purchase_location: Yup.mixed(),
          store_not_listed: Yup.boolean(),
          ...(config?.HOLDINGS?.MANDATORY_FIELDS?.PURCHASE_PRICE
            ? {
                purchase_price: Yup.object({
                  amount: Yup.number()
                    .min(1, t('message.pleaseEnterValidAmount'))
                    .required(t('message.pleaseEnterValidAmount')),
                  currency: Yup.string()
                    .trim()
                    .required(t('message.currencyIsRequired')),
                })
                  .required(t('message.pleaseEnterPurchasePrice'))
                  .typeError(t('message.pleaseEnterPurchasePrice')),
              }
            : {
                purchase_price: Yup.object({
                  amount: Yup.number(),
                  currency: Yup.string(),
                }),
              }),
          ...(config?.CLAIMS?.CLAIMS_FLOW.INSTALLATION && {
            installation_date: Yup.date()
              .required(t('message.installationDateRequired'))
              .test(
                'install-after-purchase',
                'Installation date cannot be earlier than purchase date',
                function (value) {
                  const purchase = (this.parent as any)?.purchase_time;
                  if (
                    !config?.HOLDINGS?.NEW_REGISTRATION_FLOW
                      ?.INSTALLATION_PRICE_VALIDATION
                  ) {
                    return true;
                  }
                  if (!value || !purchase) return true;
                  try {
                    const installTs = new Date(value as any).getTime();
                    const purchaseTs = new Date(purchase as any).getTime();
                    return installTs >= purchaseTs;
                  } catch (e) {
                    return true;
                  }
                }
              ),
            installation_time: Yup.string()
              .trim()
              .required(t('message.installationTimeRequired')),
          }),
          ...(config?.CLAIMS?.CLAIMS_FLOW.INSTALLATION && {
            installer_id: Yup.string(),
          }),
          ...(config?.CLAIMS.CLAIMS_FLOW.SERIAL_NUMBER && {
            serial_number: Yup.string()
              .trim()
              .matches(
                /^[A-Za-z0-9]*$/,
                t('message.serialMustBeAlphanumeric') ||
                  'Only letters and numbers are allowed'
              ),
          }),
          ...(config?.CLAIMS.CLAIMS_FLOW.SERIAL_NUMBER_PROOF && {
            serial_number_proof: Yup.array(),
          }),
          ...(config?.HOLDINGS.NEW_REGISTRATION_FLOW.PROOF_OF_PURCHASE && {
            purchase_proof: Yup.array(),
          }),
          ...(config?.HOLDINGS.MANDATORY_FIELDS.PURCHASE_TIME ||
          config?.HOLDINGS?.NEW_REGISTRATION_FLOW?.INSTALLATION_PRICE_VALIDATION
            ? {
                purchase_time: Yup.date()
                  .max(new Date(), 'Please enter a valid date')
                  .required('Please select a purchase time')
                  .typeError('Please enter a valid date'),
              }
            : {
                purchase_time: Yup.string().nullable().trim(),
              }),
          ...createDynamicYupSchema(
            config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS,
            RequiredType.REQUIRED_FOR_ALL
          ).fields,
          consumer_issue_type_ref: Yup.string()
            .trim()
            .required(t('Issue type must be selected')),
          consumer_issue_description: Yup.string().trim(),
          internal_comment: Yup.string().trim(),
        }
  );

  const SyncDates = ({
    values,
    setFieldValue,
    isEnabled,
    isInstallationFlow,
  }: any) => {
    useEffect(() => {
      if (!isEnabled || !isInstallationFlow) return;
      const purchase = values?.purchase_time;
      const install = values?.installation_date;
      if (purchase && !install) {
        setFieldValue('installation_date', purchase);
      } else if (install && !purchase) {
        setFieldValue('purchase_time', install);
      }
    }, [
      values?.purchase_time,
      values?.installation_date,
      isEnabled,
      isInstallationFlow,
    ]);
    return null;
  };

  // TODO: check this on submit func to save the case
  const onSubmit = async (values: HoldingDataCase) => {
    setIsSubmitting(true);
    setLoading(true);

    if (
      config?.CLAIMS.CLAIMS_FLOW.SERIAL_NUMBER &&
      vendableItem.serialNumberEnabled &&
      config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED
    ) {
      const serialNumber = values.serial_number;

      if (!serialNumber) {
        toast.error(t('general.pleaseEnterSerialNumber'));
        setIsSubmitting(false);
        setLoading(false);
        return;
      }

      if (!productData?.value) {
        toast.error(t('general.pleaseSelectProductFirst'));
        setIsSubmitting(false);
        setLoading(false);
        return;
      }

      const isValid = isSerialNumberValid(
        serialNumber,
        serialNumbers,
        serialNumberRanges
      );

      if (!isValid) {
        toast.error(t('message.serialNumberNotFound'));
        setIsSubmitting(false);
        setLoading(false);
        return;
      }

      try {
        const mfr = `${
          PersonaTypeSingleLetter.Manufacturer
        }${PersonaIdTypeSplitter}${t('tenantLink')}`;
        // Check if the serial number is already used in any holding proxy
        const query = FBD_HoldingProxy.query()
          .where('mfr', '==', mfr)
          .where('serial', '==', serialNumber);
        const existingHoldings = await query.getContents();

        if (existingHoldings && existingHoldings.length > 0) {
          toast.error(t('general.serialNumberAlreadyUsed'));
          setIsSubmitting(false);
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error checking serial number:', error);
        toast.error(t('general.somethingWentWrongPleaseTryAgain'));
        return;
      }
    }

    // If values include a retailer from the database, then purchase_location_other should be cleared - and vice versa
    const newPurchaseLocation =
      values.custom_store_name && values.store_not_listed
        ? null
        : values.purchase_location?.docid ?? '';

    const newPurchaseCountry =
      values.custom_store_name && values.store_not_listed
        ? values.purchase_country
        : values.purchase_location?.country ?? '';

    const newPurchaseLocationOther = values.store_not_listed
      ? values.custom_store_name ?? ''
      : null;

    let finalData: Caseflow_CaseCreation_Sage_Shelta_FormDataShape =
      data.holding
        ? {
            consumer_first_name: data.consumer.splitname?.first,
            consumer_last_name: data.consumer.splitname?.last,
            consumer_address: data.consumer.address,
            consumer_email: data.consumer.email,
            consumer_telephone: data.consumer.phone,
            consumer_preferred_contact: data.consumer.preferred_contact,
            holding_vendable_id: data.holding.vendable,
            consumer_holding_purchase_location: newPurchaseLocation,
            consumer_holding_purchase_country: newPurchaseCountry,
            consumer_holding_purchase_location_other: newPurchaseLocationOther,
            purchase_date: data.holding.purchase_time,
            consumer_issue_type:
              consumerIssueTypeOptions.find(
                (option) => option.id === values.consumer_issue_type_ref
              )?.label ?? '',
            internal_comment: values.internal_comment ?? '',
            consumer_issue_description: values.consumer_issue_description,
          }
        : ({
            ...data,
            ...values,
            consumer_holding_purchase_location: newPurchaseLocation,
            consumer_holding_purchase_country: newPurchaseCountry,
            consumer_holding_purchase_location_other: newPurchaseLocationOther,
            purchase_date: values.purchase_time
              ? OurDateTime.dateToTimestamp(values.purchase_time as any)
              : null,
            consumer_issue_type:
              consumerIssueTypeOptions.find(
                (option) => option.id === values.consumer_issue_type_ref
              )?.label ?? '',
            consumer_issue_description: values.consumer_issue_description,
          } as any);

    finalData.consumer_issue_type_ref = values.consumer_issue_type_ref;
    try {
      const res = await initiateCaseFlowCase({
        origin:
          caseFlowType == CaseflowCaseTypes.SHELTA
            ? RegisterCaseOrigins.SAGE_SHELTA
            : RegisterCaseOrigins.SAGE_LITE,
        caseType: caseFlowType,
        tenantLink: activePremiumTenantLink,
        consumerLink: data.consumer?.docid,
        holdingLink: data.holding?.holdingLink,
        formData: finalData,
        installerLink: data.holding?.installer ?? '',
        warrantorLink: GenerateTenantRootPersonaLink(
          PersonaTypeFullKeyValues.warrantor,
          activePremiumTenantLink
        ),
      });

      if (res.holdingLink && res.consumerLink) {
        onHoldingCreated({
          case_id: res.caseId,
          consumer_persona_id: res.consumerLink,
          holding_id: res.holdingLink,
        });
      }
      if (config.CLAIMS.CLAIMS_FLOW.CLAIM_UPLOAD) {
        onChange('claim-upload');
      } else {
        handleClose();
      }
    } catch (err: any) {
      console.log(err);
      toast.error(err?.message ?? t('Something went wrong'));
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      props.retailerData &&
      config.HOLDINGS.NEW_REGISTRATION_FLOW.DEFAULT_STORE
    ) {
      const locationValue = {
        docid: props.retailerData.id,
        country: props.retailerData.country,
      };
      if (formikRef && formikRef.current) {
        formikRef.current.setFieldValue('purchase_location', locationValue);
      }
    }
  }, [props.retailerData]);

  useEffect(() => {
    if (formikRef && formikRef.current && isDatePurchaseDisabled) {
      formikRef.current.setFieldValue('purchase_time', null);
    }
  }, [isDatePurchaseDisabled]);

  if (!config.CLAIMS.CLAIMS_FLOW.CREATE_PRODUCT) {
    if (!data.holding) {
      return (
        <div className="p-5">
          <p className="pb-3">{t('general.noValidProductsFound')}</p>
          <div className="flex">
            <Button kind="primary" onClick={() => onChange('select-user')}>
              {t('general.searchAgain')}
            </Button>
          </div>
        </div>
      );
    }
  }
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnChange={false}
      validateOnBlur={false}
      innerRef={formikRef}
    >
      {({ errors, values, setFieldValue }) => (
        <Form className="flex-col gap-3 px-5">
          <SyncDates
            values={values}
            setFieldValue={setFieldValue}
            isEnabled={
              config?.HOLDINGS?.NEW_REGISTRATION_FLOW
                ?.INSTALLATION_PRICE_VALIDATION
            }
            isInstallationFlow={
              !!(
                config?.HOLDINGS?.INSTALLATION_REQUIRED ||
                config?.CLAIMS?.CLAIMS_FLOW?.INSTALLATION
              )
            }
          />
          <div className="mt-4 grid grid-cols-2 gap-4">
            {!data.holding && (
              <>
                <Input
                  type="autocomplete-vendable"
                  name="holding_vendable_id"
                  label={t('Product') + '*'}
                  settings={{
                    isMulti: false,
                    id: 'holding_vendable_id',
                    placeholder: t('Product name'),
                    options: [],
                    errors: errors,
                    hint: '*' + t('required'),
                    tenantLink: t('tenantLink'),
                  }}
                  onChange={(items) => {
                    setProductData(items);
                    setProductLoaded(false);
                  }}
                />
                <div className="w-full flex-col">
                  <Input
                    type="datepicker"
                    name="purchase_time"
                    label={t('Date of purchase')}
                    settings={{
                      id: 'purchase_time',
                      placeholder: t('Date of purchase'),
                      maxDate:
                        config?.HOLDINGS?.NEW_REGISTRATION_FLOW
                          ?.INSTALLATION_PRICE_VALIDATION &&
                        (config?.HOLDINGS?.INSTALLATION_REQUIRED ||
                          config?.CLAIMS?.CLAIMS_FLOW?.INSTALLATION) &&
                        values?.installation_date
                          ? new Date(values.installation_date as any)
                          : new Date(),
                      disabled: isDatePurchaseDisabled,
                      locale:
                        (tenantInfo?.country &&
                          countryDateLocalesString[tenantInfo?.country]) ??
                        'en-GB',
                    }}
                  />
                  <div className="flex items-center pt-2">
                    <Input
                      type="checkbox"
                      name="noDateOfPurchase"
                      settings={{
                        checkboxLabel: t('Date not known'),
                        checkboxLabelStyles: 'text-base text-gray-500',
                        onChange: () =>
                          setIsDatePurchaseDisabled(!isDatePurchaseDisabled),
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          {!data.holding && config?.CLAIMS?.CLAIMS_FLOW?.INSTALLATION && (
            <div
              className={
                installerPersona?.personaId
                  ? 'mt-4 grid grid-cols-1 gap-4 md:grid-cols-2'
                  : 'mt-4 grid grid-cols-1 gap-4 md:grid-cols-3'
              }
            >
              <div className="w-full flex-col">
                <Input
                  type="datepicker"
                  name="installation_date"
                  label={`${t('general.installationDate')}*`}
                  settings={{
                    id: 'installation_date',
                    placeholder:
                      tenantInfo?.country &&
                      countryDateFormats[tenantInfo?.country]
                        ? countryDateFormats[tenantInfo?.country].toUpperCase()
                        : 'DD/MM/YYYY',
                    locale:
                      (tenantInfo?.country &&
                        countryDateLocalesString[tenantInfo?.country]) ??
                      'en-GB',
                    minDate:
                      config?.HOLDINGS?.NEW_REGISTRATION_FLOW
                        ?.INSTALLATION_PRICE_VALIDATION &&
                      (config?.HOLDINGS?.INSTALLATION_REQUIRED ||
                        config?.CLAIMS?.CLAIMS_FLOW?.INSTALLATION) &&
                      values?.purchase_time
                        ? new Date(values.purchase_time as any)
                        : undefined,
                    initialDate: new Date(),
                    hint: '*required',
                  }}
                />
              </div>
              <div className="w-full flex-col">
                <Input
                  type="time"
                  name="installation_time"
                  label={`${t('Installation time')}*`}
                  settings={{
                    id: 'installation_time',
                    placeholder: t('Installation time'),
                    hint: '*required',
                    format: 'hh:mm',
                  }}
                />
              </div>
              {!installerPersona?.personaId && (
                <Input
                  type="select"
                  label={t('Assign installer')}
                  name="installer_id"
                  settings={{
                    options: installerUsers ?? [],
                    id: 'installer_id',
                    placeholder: t('Please select installer'),
                  }}
                />
              )}
            </div>
          )}
          {!data.holding && (
            <div
              className={`mt-4 grid gap-4 ${
                data.holding ? 'grid-cols-1' : 'grid-cols-2'
              } `}
            >
              <Input
                type="currency"
                name="purchase_price"
                label={`${t('Purchase price')}${
                  config?.HOLDINGS?.MANDATORY_FIELDS?.PURCHASE_PRICE ? '*' : ''
                }`}
                settings={{
                  id: 'purchase_price',
                  placeholder: t('Purchase amount'),
                  currency: tenantInfo?.currency,
                  hint: config?.HOLDINGS?.MANDATORY_FIELDS?.PURCHASE_PRICE
                    ? '*required'
                    : '',
                }}
              />

              <div className="flex grow flex-col">
                <Input
                  type="autocomplete-location-retailer"
                  name="purchase_location"
                  label={t(
                    `general.${config.HOLDINGS.NEW_REGISTRATION_FLOW.DEFAULT_STORE_NAME}`
                  )}
                  settings={{
                    id: 'purchase_location',
                    isMulti: false,
                    placeholder: t('message.pleaseSelectAnOption'),
                    errors: errors,
                    disabled: values.store_not_listed,
                    ...(config.HOLDINGS.NEW_REGISTRATION_FLOW
                      .STORES_FROM_FIRESTORE_RETAILERS && {
                      tenantLink: t('tenantLink'),
                    }),
                  }}
                  onChange={(item) => {
                    setFieldValue('purchase_location', item?.value || null);
                  }}
                />
                <Input
                  type="checkbox"
                  name="store_not_listed"
                  settings={{
                    checkboxLabel: t('Store is not listed'),
                    checkboxLabelStyles: 'text-base text-gray-500',
                  }}
                />
                {values.store_not_listed && (
                  <div className="mt-2 flex flex-col gap-2">
                    <Input
                      type="text"
                      name="custom_store_name"
                      settings={{
                        id: 'custom_store_name',
                        placeholder: t('Enter store name'),
                      }}
                    />
                    <Input
                      type="select"
                      label=""
                      name="purchase_country"
                      settings={{
                        options: LIST_COUNTRIES,
                        id: 'purchase_country',
                        placeholder: t('Country where product was purchased'),
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          {!data.holding && config?.CLAIMS?.CLAIMS_FLOW?.SERIAL_NUMBER && (
            <div className="my-4 flex flex-col gap-2">
              <div className="relative flex items-end gap-2">
                <div className="flex-grow">
                  <Input
                    type="text"
                    label={t('Serial number')}
                    name="serial_number"
                    settings={{
                      placeholder: t('Serial number'),
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          <div
            className={`mt-4 grid gap-4  ${
              data.holding ? 'md:grid-cols-1' : 'md:grid-cols-2'
            }`}
          >
            <Input
              type="select"
              name="consumer_issue_type_ref"
              label={t('Customer issue type' + '*')}
              settings={{
                isMulti: false,
                id: 'consumer_issue_type_ref',
                placeholder: t('Product defect'),
                options: consumerIssueTypeOptions,
                errors: errors,
                hint: '*required',
              }}
            />
            <Input
              type="text"
              name="consumer_issue_description"
              label={t('Customer issue description')}
              settings={{
                id: 'consumer_issue_description',
                placeholder: t('Enter text here'),
                allowSpecialCharacter: true,
              }}
            />
          </div>
          <div className="mt-4 flex items-center gap-4">
            <Input
              type="text"
              name="internal_comment"
              label={t('Internal comment')}
              settings={{
                id: 'internal_comment',
                placeholder: t('Enter text here'),
                allowSpecialCharacter: true,
              }}
            />
          </div>

          {Object.keys(config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS)
            .length > 0 && (
            <SageFormBuilder
              fields={config.HOLDINGS.NEW_REGISTRATION_FLOW.ADDITIONAL_FIELDS}
              maxGridCount={2}
            />
          )}

          <div className="mt-5 flex w-full gap-4">
            <Button kind="primary" type="submit" loading={isSubmitting}>
              {t('Create claim')}
            </Button>
            <Button kind="outline_red" type="submit" onClick={handleClose}>
              {t('Cancel')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default NCCHF_Base;
